// Preview-first: a buyer ordered photos whose originals haven't uploaded yet. The upload manager polls the
// server's small "sold, original still owed" list (POST /admin/content {pendingOriginals, soldOnly}) every
// few seconds while a preview-first pass runs, and each running pass decides here what to do with it:
//
//   - copy pass (quick copies still uploading): the copy of a sold photo has landed (it was orderable), so
//     send its ORIGINAL now as a small prioritised originals batch instead of after every copy is up. The
//     pipeline remembers what it already sent so the originals pass that follows skips those files;
//   - originals pass: sold files still queued in it are handed to a prioritised batch (the pass skips them);
//   - prioritised batch: just holds its files.
//
// A sold key no running pass holds is MISSING LOCALLY (it was uploaded from another session, device or
// folder): the uploader says so, with the file names, so the photographer can add them.
//
// Everything here is pure; useUploadManager wires it to the jobs.

/** A file's upload state (= useUploadManager FileStatus). */
type FileStatus = "queued" | "uploading" | "done" | "error";

/** One sold photo still owed its original, as the server lists it (key relative to its collection). */
export interface SoldOriginal {
  key: string;
  collectionGuid?: string;
  soldAt?: number | null;
}

/** How often to ask while a preview-first pass runs (the server may suggest another interval). */
export const SOLD_POLL_MS = 5_000;
/** How often to ask a backend that predates the sparse sold index (it answers the full owed list). */
export const LEGACY_SOLD_POLL_MS = 60_000;
/** How often the uploader asks while nothing is uploading to the open gallery. */
export const SOLD_IDLE_POLL_MS = 30_000;
/** Every Nth poll reads the full owed list instead of the sparse sold index (picks up photos sold before
 *  the index existed). */
export const FULL_LIST_EVERY = 12;
const MAX_BACKOFF_MS = 60_000;

/** Delay before the next poll: the interval normally, doubling per consecutive failure (capped). */
export const nextSoldPollDelay = (failures: number, base = SOLD_POLL_MS): number =>
  failures <= 0 ? base : Math.min(MAX_BACKOFF_MS, base * 2 ** Math.min(failures, 6));

/** Keys sold in `folder` (a pass uploads into one collection; no folder = the gallery root). */
export const soldKeysFor = (sold: readonly SoldOriginal[], folder: string | undefined): Set<string> =>
  new Set(sold.filter((s) => !folder || !s.collectionGuid || s.collectionGuid === folder).map((s) => s.key));

export interface FileView {
  status: FileStatus;
  skipped?: boolean;
}

/** What a running pass does with this round's sold keys: which file indexes to send now as a prioritised
 *  originals batch, and which sold keys it holds (they are not "missing locally"). */
export interface SoldPlan {
  bump: number[];
  claimed: Set<string>;
}

/**
 * Copy pass. A sold photo whose copy is up gets its original sent now — unless this pipeline already
 * sent it (`sent`). A sold file whose copy is still queued/in flight is held (its original follows the
 * copy). A file whose copy failed is not held: its original can't follow from this pass.
 */
export const planCopyPass = (
  paths: readonly string[],
  files: readonly FileView[],
  sold: ReadonlySet<string>,
  sent: ReadonlySet<string>,
): SoldPlan => {
  const bump: number[] = [];
  const claimed = new Set<string>();
  if (!sold.size) return { bump, claimed };
  paths.forEach((p, i) => {
    if (!sold.has(p)) return;
    const f = files[i];
    if (!f || f.status === "error") return;
    claimed.add(p);
    if (f.status === "done" && !f.skipped && !sent.has(p)) bump.push(i);
  });
  return { bump, claimed };
};

/**
 * Main originals pass. Sold files not yet picked up by its workers (index ≥ `cursor`, still queued, not
 * handed off before) go to a prioritised batch now. Files already in flight or done are held — they are
 * on their way.
 */
export const planOriginalsPass = (
  paths: readonly string[],
  files: readonly FileView[],
  sold: ReadonlySet<string>,
  cursor: number,
  handedOff: ReadonlySet<number>,
): SoldPlan => {
  const bump: number[] = [];
  const claimed = new Set<string>();
  if (!sold.size) return { bump, claimed };
  paths.forEach((p, i) => {
    if (!sold.has(p)) return;
    const f = files[i];
    if (!f || f.status === "error") return;
    claimed.add(p);
    if (i >= cursor && f.status === "queued" && !handedOff.has(i)) bump.push(i);
  });
  return { bump, claimed };
};

/** A prioritised originals batch: holds its own sold files, never spawns another batch. */
export const planHoldOnly = (paths: readonly string[], files: readonly FileView[], sold: ReadonlySet<string>): SoldPlan => {
  const claimed = new Set<string>();
  paths.forEach((p, i) => {
    if (sold.has(p) && files[i] && files[i].status !== "error") claimed.add(p);
  });
  return { bump: [], claimed };
};

const idOf = (s: SoldOriginal) => `${s.collectionGuid ?? ""}/${s.key}`;

/** Sold photos no running pass holds. `claims` are per pass: [its folder, the keys it holds]. */
export const missingLocally = (
  sold: readonly SoldOriginal[],
  claims: ReadonlyArray<readonly [string | undefined, ReadonlySet<string>]>,
): SoldOriginal[] =>
  sold.filter((s) => !claims.some(([folder, keys]) => (!folder || !s.collectionGuid || s.collectionGuid === folder) && keys.has(s.key)));

const fileNameOf = (key: string): string => key.split("/").pop() || key;
const inFolder = (s: SoldOriginal, folder: string | undefined) => !folder || !s.collectionGuid || s.collectionGuid === folder;

/** One sold photo matched to a running pass's local file by FILE NAME: send `files[index]` as the original of `key`. */
export interface NameMatch {
  index: number;
  key: string;
}

/**
 * Sold photos no pass holds by path (`missing`) — the photographer added the same photos again from another
 * folder, so the paths differ — matched to this pass's files by FILE NAME, only when that is unambiguous:
 * exactly one sold photo in the collection has the name (over every sold photo the poll listed, `sold`),
 * exactly one file in this pass has it, and that file didn't fail. Already sent (`sent`, sold keys) are skipped.
 * The pass then sends that file as the sold photo's original, into the sold photo's key.
 */
export const planSoldByName = (
  paths: readonly string[],
  files: readonly FileView[],
  missing: readonly SoldOriginal[],
  sold: readonly SoldOriginal[],
  folder: string | undefined,
  sent: ReadonlySet<string>,
): NameMatch[] => {
  const want = missing.filter((s) => inFolder(s, folder) && !sent.has(s.key));
  if (!want.length) return [];
  const soldNames = new Map<string, number>();
  for (const s of sold) if (inFolder(s, folder)) soldNames.set(fileNameOf(s.key), (soldNames.get(fileNameOf(s.key)) ?? 0) + 1);
  const local = new Map<string, number[]>();
  paths.forEach((p, i) => {
    const name = fileNameOf(p);
    const list = local.get(name);
    if (list) list.push(i);
    else local.set(name, [i]);
  });
  const out: NameMatch[] = [];
  for (const s of want) {
    const name = fileNameOf(s.key);
    if (soldNames.get(name) !== 1) continue;
    const idx = local.get(name);
    if (!idx || idx.length !== 1) continue;
    const f = files[idx[0]];
    if (!f || f.status === "error" || paths[idx[0]] === s.key) continue;
    out.push({ index: idx[0], key: s.key });
  }
  return out;
};

/** Same sold set (order-insensitive)? Used to avoid re-rendering on every unchanged poll. */
export const sameSold = (a: readonly SoldOriginal[], b: readonly SoldOriginal[]): boolean => {
  if (a.length !== b.length) return false;
  const ids = new Set(a.map(idOf));
  return b.every((s) => ids.has(idOf(s)));
};

/** File name for display (the last path segment of a collection-relative key). */
export const soldFileName = (key: string): string => key.split("/").pop() || key;

/** "IMG_1.jpg, IMG_2.jpg and 3 more" — the first few names for the uploader's notice. */
export const soldNamesLabel = (sold: readonly SoldOriginal[], show = 3): string => {
  const names = sold.map((s) => soldFileName(s.key));
  if (names.length <= show) {
    return names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, show).join(", ")} and ${names.length - show} more`;
};

/** The uploader's notice: which sold photos a buyer is waiting on that this uploader can't send. */
export const buyerWaitingText = (missing: readonly SoldOriginal[], uploading: boolean): string => {
  const n = missing.length;
  const what = n === 1 ? "1 original" : `${n.toLocaleString()} originals`;
  const where = uploading ? (n === 1 ? " that isn't in this upload" : " that aren't in this upload") : "";
  return `A buyer is waiting for ${what}${where}: ${soldNamesLabel(missing)}. Add the folder with ${n === 1 ? "it" : "them"} and ${n === 1 ? "it goes" : "they go"} first.`;
};
